import { injectable, singleton } from "tsyringe";
import { Logger } from "../utils/Logger";
import { Buffer } from "buffer";
import { UserConsent } from "./UserConsent";
import { ConsentStringV2 } from "./ConsentString";
import { getUserAdapter } from "../gtm/GtmUtils";
import { ConsentError } from "./ConsentError";

/**
 * CmpService is responsible for managing user consent information.
 * It retrieves the consent string from the data layer, decodes it,
 * and updates the UserConsent property with the user's preferences.
 */
@injectable()
@singleton()
export class CmpService {
  public UserConsent: UserConsent;
  public ConsentHandled: boolean = false;

  constructor() {
    this.UserConsent = {
      targetedAds: undefined,
      targetedContent: undefined,
    };
  }

  /**
   * Retrieves and processes the user consent string.
   * Decodes the consent string if available and updates the UserConsent property
   * with the user's preferences for targeted ads and content.
   * @returns A Promise that resolves when consent processing is complete
   */
  public fetchConsent(): void {
    const user = getUserAdapter();
    if (!user?.consent?.consentString) {
      this.handleConsentError(user?.consent?.error);
      this.ConsentHandled = true;
      return;
    }

    if (user?.consent?.consentString && user?.consent?.error) {
      Logger.error("Consent string is not empty, but an error code was provided: " + user.consent.error);
    }
    const decodedConsent = this.decodeBase64ToV2ConsentObject(user.consent.consentString);
    if (decodedConsent) {
      this.UserConsent = {
        targetedAds: decodedConsent.purposes.ads,
        targetedContent: decodedConsent.purposes.content,
      };
    }
    this.ConsentHandled = true;
  }

  /***
   * Handles errors related to consent string retrieval.
   * Logs appropriate messages based on the type of error encountered.
   * @param error The ConsentError encountered during consent string retrieval
   */
  private handleConsentError(error: ConsentError | undefined): void {
    if (error === ConsentError.OutsideCanada) {
      Logger.warn("Consent string is empty, user is outside Canada, no consent required");
    } else if (error === ConsentError.CmpFail) {
      Logger.warn("Consent string is empty, CMP failed to load, no consent required");
    } else if (error) {
      Logger.error(`Consent string is empty, unknown CMP error: ${error}`);
    } else {
      Logger.error("Consent string is undefined, no reason given");
    }
  }

  /**
   * Decodes a base64 encoded consent string to a ConsentStringV2 object.
   * @param base64String The base64 encoded consent string
   * @returns The decoded ConsentStringV2 object or undefined if decoding fails
   */
  private decodeBase64ToV2ConsentObject(base64String: string): ConsentStringV2 | undefined {
    const version = base64String[0];
    if (version !== "2") {
      Logger.error(`Unsupported consent string version: ${version}`);
      return undefined;
    }

    const decodedString = Buffer.from(base64String.slice(1), "base64").toString("utf-8");

    try {
      const parsedObject = JSON.parse(decodedString);
      if (this.isConsentStringV2(parsedObject)) {
        return parsedObject;
      } else {
        Logger.error(
          `Parsed consent string doesn't match expected ConsentStringV2 structure. Received JSON : ${JSON.stringify(
            parsedObject,
          )}`,
        );
        return undefined;
      }
    } catch {
      Logger.error(`Unable to parse consent string as JSON: ${base64String}`);
      return undefined;
    }
  }

  /**
   * Type guard to check if the parsed object conforms to the ConsentStringV2 interface
   * @param obj The object to check
   * @returns True if the object is a valid ConsentStringV2, false otherwise
   */
  private isConsentStringV2(obj: unknown): obj is ConsentStringV2 {
    if (!obj || typeof obj !== "object") {
      return false;
    }

    const consent = obj as Record<string, unknown>;

    if (!consent.purposes || typeof consent.purposes !== "object") {
      return false;
    }

    const purposes = consent.purposes as Record<string, unknown>;

    return (
      "ads" in purposes &&
      typeof purposes.ads === "boolean" &&
      "content" in purposes &&
      typeof purposes.content === "boolean"
    );
  }

  isCmpEnabled(): boolean {
    const user = getUserAdapter();
    return user?.consent?.enabled ?? false;
  }
}
